<script setup lang="ts">
import { computed } from 'vue'
import ActionButton from '../common/ActionButton.vue'
import RatingDistributionPanel from '../common/RatingDistributionPanel.vue'
import SectionHeader from '../common/SectionHeader.vue'
import SalaryCard from './salary/SalaryCard.vue'
import AIReviewCard from './review/AIReviewCard.vue'
import type { RatingsDistribution } from '~~/server/types/company'
import { generateSalaryDescription } from '~/utils/salaryDescription'
import { useGlobalSalaryPeriod, provideGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
interface Props {
  company: any
  userZipCode: string
  preFetchedData?: any
  logo_url?: string
  brand_color?: string
}
const props = withDefaults(defineProps<Props>(), {
  preFetchedData: undefined,
  logo_url: '',
  brand_color: '#5F0CE2'
})
const companyName = computed(() => {
  return props.company?.brand_name || props.company?.company_name || ''
})
const ratingsDistribution = computed<RatingsDistribution>(() => {
  return (
    props.company?.ratings_distribution || {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  )
})
const totalRatings = computed<number>(() => {
  if (!props.company?.ratings_distribution) return 0
  return Object.values(
    props.company.ratings_distribution as Record<string, number>
  ).reduce((sum: number, value: number) => sum + value, 0)
})
const overallRating = computed<number>(() => {
  return props.company?.overall_rating || 0
})
const overviewText = computed(() => {
  return (
    props.company?.description
    || `Learn about working at ${companyName.value}, including employee reviews, salaries, and job opportunities. Find out if ${companyName.value} is the right workplace for you.`
  )
})
const preFetchedAIReview = computed(() => {
  return props.preFetchedData?.aiReviewData || null
})
const aiReviewPending = computed(() => {
  return !props.preFetchedData?.aiReviewData
})
const preFetchedSalaryData = computed(() => {
  return props.preFetchedData?.salaryData || null
})
const salaryPending = computed(() => {
  return !props.preFetchedData?.salaryData
})
const preFetchedJobsData = computed(() => {
  return props.preFetchedData?.jobsData || { jobs: [], total: 0, available: 0 }
})
const jobsPending = computed(() => {
  return !props.preFetchedData?.jobsData
})
const processedJobsData = computed(() => {
  return preFetchedJobsData.value || { jobs: [], total: 0 }
})
const router = useRouter()
const salaryPeriodState = provideGlobalSalaryPeriod()
const { activePeriod, adjustValueForPeriod } = salaryPeriodState
const salaryDescription = computed(() => {
  let salaryData = null
  if (
    preFetchedSalaryData.value
    && 'data' in preFetchedSalaryData.value
    && preFetchedSalaryData.value.data
  ) {
    salaryData = preFetchedSalaryData.value.data
  }
  return generateSalaryDescription(
    salaryData,
    companyName.value,
    {},
    '',
    adjustValueForPeriod
  )
})
</script>
<template>
  <div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <div class="mb-8">
        <SectionHeader :title="`Is ${companyName} a good place to work?`" class="mb-6" />
        <div v-if="salaryPending || jobsPending || aiReviewPending" class="animate-pulse space-y-4 min-h-[200px]">
          <div class="h-40 bg-gray-200 rounded" />
        </div>
        <RatingDistributionPanel v-else-if="totalRatings > 0" :ratings_distribution="ratingsDistribution"
          :total_ratings="totalRatings" :overall_rating="overallRating" :left-panel-title="'Company Overview'"
          :left-panel-content="overviewText" class="mb-8" />
        <CompanyDetailsGrid :company="props.company" />
      </div>
      <div class="mb-8">
        <SectionHeader :title="`${companyName} Employee Reviews Overview`" class="mb-6">
          <template #action>
            <NuxtLink :to="`/company/${props.company.permalink}/reviews`" class="hidden sm:block">
              <ActionButton text="See all reviews" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
                :brand-color="props.brand_color" />
            </NuxtLink>
          </template>
        </SectionHeader>
        <div class="block sm:hidden mb-4">
          <NuxtLink :to="`/company/${props.company.permalink}/reviews`">
            <ActionButton text="See all reviews" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" class="justify-start" />
          </NuxtLink>
        </div>
        <AIReviewCard :company-name="companyName" :permalink="company.permalink" :national-level="true"
          :pre-fetched-review="preFetchedAIReview && 'aiReview' in preFetchedAIReview
              ? preFetchedAIReview.aiReview
              : null
            " :is-loading="aiReviewPending" />
      </div>
      <div class="mb-8">
        <SectionHeader :title="`Pay Overview at ${companyName}`" class="mb-6">
          <template #action>
            <NuxtLink :to="`/company/${props.company.permalink}/salaries`" class="hidden sm:block">
              <ActionButton text="See all salaries" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
                :brand-color="props.brand_color" />
            </NuxtLink>
          </template>
        </SectionHeader>
        <div class="block sm:hidden mb-4">
          <NuxtLink :to="`/company/${props.company.permalink}/salaries`">
            <ActionButton text="See all salaries" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" class="justify-start" />
          </NuxtLink>
        </div>
        <SalaryCard :company-name="companyName" :permalink="props.company.permalink" :pre-fetched-data="preFetchedSalaryData && 'data' in preFetchedSalaryData
            ? preFetchedSalaryData.data
            : null
          " :is-loading="salaryPending" :force-server-render="true" :description-text="salaryDescription" />
      </div>
      <div class="mb-8">
        <SectionHeader :title="`Latest Job Openings at ${companyName}`" class="mb-6">
          <template #action>
            <NuxtLink :to="`/company/${props.company.permalink}/jobs`" class="hidden sm:block">
              <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
                :brand-color="props.brand_color" />
            </NuxtLink>
          </template>
        </SectionHeader>
        <div class="block sm:hidden mb-4">
          <NuxtLink :to="`/company/${props.company.permalink}/jobs`">
            <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" class="justify-start" />
          </NuxtLink>
        </div>
        <CompanyTopJobs :permalink="props.company.permalink" :location="props.userZipCode"
          :company_name="props.company.jobs_company_name || companyName" :pre-fetched-data="processedJobsData"
          :is-loading="jobsPending" :logo_url="props.logo_url" />
      </div>
    </div>
  </div>
</template>